import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import Map, { Marker, NavigationControl, GeolocateControl } from 'react-map-gl';
import { MAPBOX_TOKEN, MAP_STYLE, DEFAULT_ZOOM, DEFAULT_CENTER, MARKER_COLOR } from '../../config/mapbox';
import { FaMapMarkerAlt } from 'react-icons/fa';
import 'mapbox-gl/dist/mapbox-gl.css';

const ProviderMap = ({ providers, userLocation, onProviderClick, onMapClick }) => {
  const [viewState, setViewState] = useState({
    longitude: userLocation?.longitude || DEFAULT_CENTER[0],
    latitude: userLocation?.latitude || DEFAULT_CENTER[1],
    zoom: DEFAULT_ZOOM
  });
  const mapRef = useRef();

  // Update map center when user location changes
  useEffect(() => {
    if (userLocation && userLocation.latitude && userLocation.longitude) {
      setViewState(prev => ({
        ...prev,
        longitude: userLocation.longitude,
        latitude: userLocation.latitude,
        zoom: DEFAULT_ZOOM
      }));
    }
  }, [userLocation]);

  // Memoize marker click handler
  const handleMarkerClick = useCallback((provider, e) => {
    e.originalEvent.stopPropagation();
    if (onProviderClick) {
      onProviderClick(provider);
    }
  }, [onProviderClick]);

  // Memoize map click handler
  const handleMapClick = useCallback(() => {
    if (onMapClick) {
      onMapClick();
    }
  }, [onMapClick]);

  // Memoize providers with valid locations
  const validProviders = useMemo(() => {
    return providers.filter(provider => 
      provider.profile?.location?.latitude && 
      provider.profile?.location?.longitude
    );
  }, [providers]);

  // Memoize user location marker
  const userLocationMarker = useMemo(() => {
    if (!userLocation?.latitude || !userLocation?.longitude) return null;
    
    return (
      <Marker
        longitude={userLocation.longitude}
        latitude={userLocation.latitude}
        anchor="bottom"
      >
        <div
          style={{
            width: '20px',
            height: '20px',
            borderRadius: '50%',
            backgroundColor: '#4285F4',
            border: '3px solid white',
            boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
            transform: 'translateZ(0)', // Force GPU acceleration
            willChange: 'transform'
          }}
        />
      </Marker>
    );
  }, [userLocation]);

  // Memoize provider markers
  const providerMarkers = useMemo(() => {
    return validProviders.map((provider) => (
      <Marker
        key={provider.id}
        longitude={provider.profile.location.longitude}
        latitude={provider.profile.location.latitude}
        anchor="bottom"
        onClick={(e) => handleMarkerClick(provider, e)}
      >
        <div 
          className="map-marker" 
          style={{ 
            cursor: 'pointer',
            transform: 'translateZ(0)', // Force GPU acceleration
            willChange: 'transform'
          }}
        >
          <FaMapMarkerAlt
            size={32}
            color={MARKER_COLOR}
            style={{
              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
              transform: 'translateY(-50%)'
            }}
          />
        </div>
      </Marker>
    ));
  }, [validProviders, handleMarkerClick]);

  return (
    <Map
      ref={mapRef}
      {...viewState}
      onMove={evt => setViewState(evt.viewState)}
      mapboxAccessToken={MAPBOX_TOKEN}
      style={{ width: '100%', height: '100%' }}
      mapStyle={MAP_STYLE}
      onClick={handleMapClick}
      reuseMaps // Reuse map instances for better performance
      optimizeForTerrain={false} // Disable terrain optimization for better performance
    >
      <NavigationControl position="top-right" />
      <GeolocateControl
        position="top-right"
        trackUserLocation
        showUserHeading
        showAccuracyCircle
      />

      {/* User location marker */}
      {userLocationMarker}

      {/* Provider markers */}
      {providerMarkers}
    </Map>
  );
};

export default React.memo(ProviderMap); 